import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import qs from 'qs';
import surveyAnswersAPIs from '../../api/surveyAnswers';
import coursesAPIs from '../../api/courses';
import sectionsAPIs from '../../api/section';
import FiltersBar from '../../components/Filters/FiltersBar';
import Pagination from '../../components/Pagination';
import TitlePage from '../../components/TitlePage';
import H1Styled from '../../components/Typography/H1Styled';
import { useAuth } from '../../contexts/Auth';
import { FilterType } from '../../interfaces/filters';
import { isDelegato } from '../../libs/utils/auth';
import { SURVEY_ANSWER_OPTIONS } from '../../interfaces/surveyAnswers';
import SurveyAnswersTable from './Tables/SurveyAnswersTable';

const { REACT_APP_DEBOUNCE_DELAY } = process.env;
const delay = Number(REACT_APP_DEBOUNCE_DELAY);

const parseMultiselectValues = (rawValue: string | null) =>
  Object.entries(qs.parse(rawValue + ''))
    .map(([, value]) => value)
    .map((elem: any) => elem.value);

export default function StatisticheCorsi() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ token, profile }] = useAuth();
  const [debounceSearchParams] = useDebounce(searchParams, delay);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (!searchParams.has('pageSize')) {
      newParams.set('pageSize', '10');
    }
    setSearchParams(newParams);
  }, []);

  const query = useQuery(['surveyAnswers', ...debounceSearchParams], () => {
    const sectionIds = parseMultiselectValues(
      debounceSearchParams.get('sections')
    );
    const courseIds = parseMultiselectValues(
      debounceSearchParams.get('courses')
    );
    const answers = parseMultiselectValues(
      debounceSearchParams.get('answers')
    );

    const courseFilters = {
      ...(sectionIds.length > 0 && { section: { id: { $in: sectionIds } } }),
      ...(courseIds.length > 0 && { id: { $in: courseIds } }),
    };

    const searchQuery = {
      filters: {
        ...(Object.keys(courseFilters).length > 0 && {
          course: courseFilters,
        }),
        ...(answers.length > 0 && { answer: { $in: answers } }),
      },
      populate: ['profile', 'course.section'],
      pagination: {
        page: searchParams.get('page'),
        pageSize: searchParams.get('pageSize'),
      },
      sort: 'createdAt:desc',
    };

    return surveyAnswersAPIs.find({ token, query: searchQuery });
  });

  return (
    <>
      <TitlePage title='Statistiche corsi' />
      <H1Styled>Statistiche corsi</H1Styled>

      <div className='space-y-5'>
        <FiltersBar
          filters={[
            {
              type: FilterType.MULTISELECT,
              attribute: 'sections',
              label: 'Sezione',
              key: 'surveyAnswersSectionsFilter',
              source: (data: any) =>
                sectionsAPIs.findOptions({
                  ...data,
                  query: {
                    ...data.query,
                    ...(isDelegato(profile)
                      ? { filters: { id: profile?.section.data?.id } }
                      : {}),
                  },
                }),
              searchForAttributes: ['name'],
            },
            {
              type: FilterType.MULTISELECT,
              attribute: 'courses',
              label: 'Corso',
              key: 'surveyAnswersCoursesFilter',
              source: (data: any) =>
                coursesAPIs.filtersFind({ ...data, query: { ...data.query } }),
              searchForAttributes: ['title'],
            },
            {
              type: FilterType.MULTISELECT,
              attribute: 'answers',
              label: 'Risposta',
              key: 'surveyAnswersAnswersFilter',
              source: () => ({ data: SURVEY_ANSWER_OPTIONS }),
              isSearchable: false,
            },
          ]}
        />
        <SurveyAnswersTable query={query} />
        <Pagination pagination={query.data?.meta.pagination} />
      </div>
    </>
  );
}
